import { collection, doc, setDoc, getDoc, getDocs, query, where, addDoc, serverTimestamp, onSnapshot, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import db from './firebaseConfig';
import "firebase/firestore";

/**
 * Check if a team name already exists in a given collection.
 */
export const checkTeamExists = async (teamName, collectionName) => {
    try {
        const teamRef = doc(db, collectionName, teamName);
        const teamDoc = await getDoc(teamRef);
        return teamDoc.exists();
    } catch (error) {
        console.error('Error checking team existence:', error);
        return false;
    }
};

/**
 * Fetch all team submissions from a Firestore collection.
 * Requires the user to be authenticated (professor login) for collection-level reads.
 */
export const getAllTeamSubmissions = async (collectionName = 'Master', gameId = null) => {
    try {
        const collectionRef = collection(db, collectionName);
        let snapshot;

        if (gameId) {
            const q = query(collectionRef, where('gameId', '==', gameId));
            snapshot = await getDocs(q);
        } else {
            snapshot = await getDocs(collectionRef);
        }

        const teams = [];
        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const hasSubmitted = data.rounds?.some(r => r.is_submitted === true);
            if (hasSubmitted) {
                teams.push({ teamName: docSnap.id, teamData: data });
            }
        });

        console.log(`Fetched ${teams.length} submitted teams from ${collectionName}${gameId ? ` (game: ${gameId})` : ''}`);
        return teams;
    } catch (error) {
        console.error('Error fetching team submissions:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time updates of team submissions.
 * Returns an unsubscribe function.
 */
export const subscribeToTeamSubmissions = (collectionName = 'Master', gameId = null, onUpdate, onError) => {
    const collectionRef = collection(db, collectionName);
    let q;
    if (gameId) {
        q = query(collectionRef, where('gameId', '==', gameId));
    } else {
        q = collectionRef;
    }

    return onSnapshot(q, (snapshot) => {
        const teams = [];
        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const hasSubmitted = data.rounds?.some(r => r.is_submitted === true);
            if (hasSubmitted) {
                teams.push({ teamName: docSnap.id, teamData: data });
            }
        });
        onUpdate(teams);
    }, (error) => {
        console.error('Snapshot listener error:', error);
        if (onError) onError(error);
    });
};

export const createTeamDocument = async (teamName, teamType, gameId = null) => {
    const teamData = {
        rounds: [
            {
                roundNumber: 1,
                submissionType: teamType,
                manufacturing: {
                    attributes: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                    total_cost: 8,
                    upgraded_bike: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                },
                rnd: {
                    upgrades: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                    },
                },
                advertising: {
                    segments: [],
                    performance_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                    brand_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                },
                profit_margin: {
                    profit_margin_selection: 1,
                    profit_margin_percent: 10,
                    cost_per_bike_dollars: 90,
                    profit_margin_per_bike_dollars: 9,
                    price_per_bike_dollars: 99,
                },
                is_submitted: false,
            },
            {
                roundNumber: 2,
                submissionType: teamType,
                manufacturing: {
                    attributes: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                    total_cost: 9,
                    upgraded_bike: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                },
                rnd: {
                    upgrades: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                    },
                },
                advertising: {
                    segments: [],
                    performance_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                    brand_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                },
                profit_margin: {
                    profit_margin_selection: 1,
                    profit_margin_percent: 10,
                    cost_per_bike_dollars: 90,
                    profit_margin_per_bike_dollars: 9,
                    price_per_bike_dollars: 99,
                },
                is_submitted: false,
            },
            {
                roundNumber: 3,
                submissionType: teamType,
                manufacturing: {
                    attributes: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                    total_cost: 9,
                    upgraded_bike: {
                        Speed: 1,
                        Comfort: 1,
                        BatteryRange: 1,
                        AllTerrain: 1,
                        Security: 1,
                        SmartFeatures: 1,
                        Storage: 1,
                        Safety: 1,
                    },
                },
                rnd: {
                    upgrades: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                    },
                },
                advertising: {
                    segments: [],
                    performance_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                    brand_ads: {
                        Speed: 0,
                        Comfort: 0,
                        BatteryRange: 0,
                        AllTerrain: 0,
                        Security: 0,
                        SmartFeatures: 0,
                        Storage: 0,
                        Safety: 0,
                        Broad: 0,
                    },
                },
                profit_margin: {
                    profit_margin_selection: 1,
                    profit_margin_percent: 10,
                    cost_per_bike_dollars: 90,
                    profit_margin_per_bike_dollars: 9,
                    price_per_bike_dollars: 99,
                },
                is_submitted: false,
            },
        ],
    };

    if (gameId) {
        teamData.gameId = gameId;
    }

    try {
        console.log('Firestore instance:', db);

        // Reference to the team document
        const teamsCollectionRef = collection(db, teamType);
        const teamRef = doc(teamsCollectionRef, teamName);

        // Check if the team document already exists
        const teamDoc = await getDoc(teamRef);
        if (teamDoc.exists()) {
            console.log(`Team document for ${teamName} already exists. Skipping creation.`);
            return; // Exit function if document already exists
        }

        // If document does not exist, create it
        await setDoc(teamRef, teamData);
        console.log(`Team document for ${teamName} created successfully.`);
    } catch (error) {
        console.error('Error creating team document:', error);
    }
};

/**
 * Create a new game in the 'games' collection.
 */
export const createGame = async (name, professorUid) => {
    const gameCode = Math.random().toString(36).substring(2, 8).toUpperCase();

    const gameData = {
        name,
        createdBy: professorUid,
        createdAt: serverTimestamp(),
        gameCode,
        simulationType: 'cuban-bikes',
        status: 'active',
    };

    const gamesRef = collection(db, 'games');
    const docRef = await addDoc(gamesRef, gameData);
    return { gameId: docRef.id, gameCode };
};

/**
 * Look up a game by its short game code.
 */
export const getGameByCode = async (gameCode) => {
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, where('gameCode', '==', gameCode.toUpperCase()));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    return { gameId: docSnap.id, ...docSnap.data() };
};

/**
 * Fetch ALL teams in a game (including those who haven't submitted any round).
 * Useful for showing a team roster with submission status.
 */
export const getAllTeamsInGame = async (collectionName = 'Master', gameId = null) => {
    try {
        const collectionRef = collection(db, collectionName);
        let snapshot;

        if (gameId) {
            const q = query(collectionRef, where('gameId', '==', gameId));
            snapshot = await getDocs(q);
        } else {
            snapshot = await getDocs(collectionRef);
        }

        const teams = [];
        snapshot.forEach((docSnap) => {
            teams.push({ teamName: docSnap.id, teamData: docSnap.data() });
        });

        return teams;
    } catch (error) {
        console.error('Error fetching all teams:', error);
        throw error;
    }
};

/**
 * Fetch all games created by a specific professor.
 */
export const getGamesByProfessor = async (professorUid) => {
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, where('createdBy', '==', professorUid));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(docSnap => ({
        gameId: docSnap.id,
        ...docSnap.data(),
    }));
};

/**
 * Update locked rounds for a game.
 * @param {string} gameId
 * @param {number[]} lockedRounds - array of round numbers that are locked (e.g., [1, 2])
 */
export const updateLockedRounds = async (gameId, lockedRounds) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { lockedRounds });
};

/**
 * Update released rounds for a game (controls student results visibility).
 * @param {string} gameId
 * @param {number[]} releasedRounds - array of round numbers whose results are visible to students
 */
export const updateReleasedRounds = async (gameId, releasedRounds) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { releasedRounds });
};

export const updateActiveTeams = async (gameId, activeTeams) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { activeTeams: activeTeams || null });
};

export const updateActiveRounds = async (gameId, activeRounds) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { activeRounds: activeRounds || null });
};

/**
 * Fetch a game document by its ID.
 */
export const getGameById = async (gameId) => {
    const gameRef = doc(db, 'games', gameId);
    const docSnap = await getDoc(gameRef);
    if (!docSnap.exists()) return null;
    return { gameId: docSnap.id, ...docSnap.data() };
};

/**
 * Subscribe to a game document for real-time updates (e.g., locked rounds).
 */
export const subscribeToGame = (gameId, onUpdate) => {
    const gameRef = doc(db, 'games', gameId);
    return onSnapshot(gameRef, (docSnap) => {
        if (docSnap.exists()) {
            onUpdate({ gameId: docSnap.id, ...docSnap.data() });
        }
    });
};

/**
 * Fetch a single team document by name.
 * @param {string} teamName - The team name (document ID)
 * @param {string} collectionName - 'Master' or 'Test'
 * @returns {Object|null} - { teamName, teamData } or null if not found
 */
export const getTeamDocument = async (teamName, collectionName) => {
    try {
        const teamRef = doc(db, collectionName, teamName);
        const teamDoc = await getDoc(teamRef);
        if (!teamDoc.exists()) return null;
        return { teamName: teamDoc.id, teamData: teamDoc.data() };
    } catch (error) {
        console.error('Error fetching team document:', error);
        return null;
    }
};

/**
 * Update the name of a game.
 */
export const updateGameName = async (gameId, newName) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { name: newName });
};

/**
 * Update the status of a game ('active' or 'completed').
 */
export const updateGameStatus = async (gameId, status) => {
    const gameRef = doc(db, 'games', gameId);
    await updateDoc(gameRef, { status });
};

/**
 * Delete a game document. Does NOT cascade to team documents.
 */
export const deleteGame = async (gameId) => {
    const gameRef = doc(db, 'games', gameId);
    await deleteDoc(gameRef);
};

/**
 * Rename a team by creating a new document with the new name and deleting the old one.
 * Uses a batch write for atomicity.
 */
export const renameTeam = async (oldName, newName, collectionName) => {
    const oldRef = doc(db, collectionName, oldName);
    const newRef = doc(db, collectionName, newName);

    const oldSnap = await getDoc(oldRef);
    if (!oldSnap.exists()) {
        throw new Error(`Team "${oldName}" does not exist.`);
    }

    const newSnap = await getDoc(newRef);
    if (newSnap.exists()) {
        throw new Error(`Team "${newName}" already exists.`);
    }

    const batch = writeBatch(db);
    batch.set(newRef, oldSnap.data());
    batch.delete(oldRef);
    await batch.commit();
};

/**
 * Delete a team document.
 */
export const deleteTeam = async (teamName, collectionName) => {
    const teamRef = doc(db, collectionName, teamName);
    await deleteDoc(teamRef);
};

/**
 * Re-open a round for a team by setting is_submitted to false.
 * @param {string} teamName
 * @param {string} collectionName
 * @param {number} roundIndex - 0-based index (round 1 = 0)
 */
export const unsubmitRound = async (teamName, collectionName, roundIndex) => {
    const teamRef = doc(db, collectionName, teamName);
    const snap = await getDoc(teamRef);
    if (!snap.exists()) {
        throw new Error(`Team "${teamName}" does not exist.`);
    }
    const data = snap.data();
    const rounds = [...data.rounds];
    rounds[roundIndex] = { ...rounds[roundIndex], is_submitted: false };
    await updateDoc(teamRef, { rounds });
};

/**
 * Update a specific field within a team's round data.
 * @param {string} teamName
 * @param {string} collectionName
 * @param {number} roundIndex - 0-based index
 * @param {object} updates - object with fields to merge into the round (e.g., { 'profit_margin': { ... } })
 */
export const updateTeamRoundData = async (teamName, collectionName, roundIndex, updates) => {
    const teamRef = doc(db, collectionName, teamName);
    const snap = await getDoc(teamRef);
    if (!snap.exists()) {
        throw new Error(`Team "${teamName}" does not exist.`);
    }
    const data = snap.data();
    const rounds = [...data.rounds];
    rounds[roundIndex] = { ...rounds[roundIndex], ...updates };
    await updateDoc(teamRef, { rounds });
};

/**
 * Save an AI insight to the cache subcollection.
 * @param {string} gameId
 * @param {string} docId - e.g., 'instructor' or 'student_TeamName'
 * @param {string} content - markdown content
 * @param {string} filterHash - e.g., 'teams:all|rounds:1,2,3'
 */
export const saveAiInsight = async (gameId, docId, content, filterHash) => {
    const ref = doc(db, 'games', gameId, 'aiInsights', docId);
    await setDoc(ref, { content, generatedAt: new Date().toISOString(), filterHash });
};

/**
 * Get a cached AI insight.
 * @param {string} gameId
 * @param {string} docId
 * @returns {Object|null} - { content, generatedAt, filterHash } or null
 */
export const getAiInsight = async (gameId, docId) => {
    const ref = doc(db, 'games', gameId, 'aiInsights', docId);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
};

/**
 * Create an instructor profile document.
 * @param {string} uid - Firebase Auth UID
 * @param {Object} profile - { name, email, institution }
 */
export const createInstructorProfile = async (uid, { name, email, institution }) => {
    const ref = doc(db, 'instructors', uid);
    await setDoc(ref, {
        name,
        email,
        institution,
        role: 'instructor',
        createdAt: serverTimestamp(),
    });
};

/**
 * Get an instructor profile by UID.
 * @param {string} uid - Firebase Auth UID
 * @returns {Object|null} - instructor profile data or null
 */
export const getInstructorProfile = async (uid) => {
    const ref = doc(db, 'instructors', uid);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
};

/**
 * Create an access key document.
 * @param {string} keyString - The key string (used as document ID)
 * @param {Object} data - { instructorId, type, status, expiresAt, maxGames, metadata }
 */
export const createAccessKey = async (keyString, data) => {
    const ref = doc(db, 'accessKeys', keyString);
    await setDoc(ref, {
        ...data,
        createdAt: serverTimestamp(),
    });
    return keyString;
};

/**
 * Validate an access key for an instructor.
 * Returns { valid: true, key, data } or { valid: false, reason }.
 * If no key exists (e.g., legacy professor), defaults to valid for backward compatibility.
 * @param {string} instructorUid
 */
export const validateAccessKey = async (instructorUid) => {
    const keysRef = collection(db, 'accessKeys');
    const q = query(keysRef, where('instructorId', '==', instructorUid), where('status', '==', 'active'));
    const snapshot = await getDocs(q);

    // Backward compatibility: if no keys exist for this instructor, allow access
    if (snapshot.empty) return { valid: true, reason: 'legacy' };

    const keyDoc = snapshot.docs[0];
    const keyData = keyDoc.data();

    if (keyData.expiresAt && keyData.expiresAt.toDate() < new Date()) {
        return { valid: false, reason: 'expired' };
    }

    return { valid: true, key: keyDoc.id, data: keyData };
};